/**
 * The port notice (D163, platform.md §6): a newer version of the game is
 * published, the session could not follow it silently, and the shell says
 * what changed and asks — update, or stay.
 *
 * @remarks
 * Pure: every word arrives as a prop, because the words are the shell's
 * (the platform's voice, never the game's — the same rule as the account
 * control, D157 addendum) and the design lane's to re-cut. The component
 * owns only the shape: a status region, so a screen reader hears the
 * question when it lands, with the two answers as buttons — and both
 * disabled while one is being answered, so a double click cannot say
 * "update" twice. Keeping comes first (D248): it is the answer that cannot
 * cost the player anything, and the first button is the one a hurried
 * reader takes.
 *
 * @packageDocumentation
 */

/** The two answers. */
export type PortNoticeChoice = 'stay' | 'update';

/** Props for {@link PortNoticeComponent}. */
export interface PortNoticeComponentProps {
	/** What changed, in the player's terms — one or more sentences. */
	readonly detail: string;
	/** The one-line statement above the detail: what moving to the new version would do. */
	readonly heading: string;
	/** Whether an answer is on its way to the host; both buttons wait. */
	readonly isAnswering: boolean;
	/** The player's answer. */
	readonly onChoose: (choice: PortNoticeChoice) => void;
	readonly stayLabel: string;
	readonly updateLabel: string;
}

/**
 * Render the notice.
 *
 * @param props - {@link PortNoticeComponentProps}
 * @returns The notice
 */
export const PortNoticeComponent = ({ detail, heading, isAnswering, onChoose, stayLabel, updateLabel }: PortNoticeComponentProps) => (
	<div className="port-notice" role="status">
		<p className="port-notice__heading">{heading}</p>
		<p className="port-notice__detail">{detail}</p>
		<div className="port-notice__choices">
			<button
				className="port-notice__choice"
				disabled={isAnswering}
				onClick={() => {
					onChoose('stay');
				}}
				type="button"
			>
				{stayLabel}
			</button>
			<button
				className="port-notice__choice port-notice__choice--update"
				disabled={isAnswering}
				onClick={() => {
					onChoose('update');
				}}
				type="button"
			>
				{updateLabel}
			</button>
		</div>
	</div>
);
